import { IQuestionnaireQuestion } from '../interfaces/Questionnaire/IQuestionnaire';

// Items a scale's own rules score 0 without an answer, by the same rules as
// the backend's QuestionnaireScoreService. The form locks these items, Submit
// leaves out any answer they kept from before they were locked, and Preview &
// Save counts them as done.

type AnswerReader = (controlName: string) => unknown;

const FUGL_MEYER_TEMPLATE_ID = 20;
const TRUNK_IMPAIRMENT_SCALE_TEMPLATE_ID = 22;

// Fugl-Meyer: part V only counts once part IV is full, 6 of 6. Until then it
// scores 0 and needs no answer.
export const FUGL_MEYER_PART_IV_ITEMS = [
  'question740',
  'question741',
  'question742',
];
export const FUGL_MEYER_PART_IV_FULL_SCORE = 6;
export const FUGL_MEYER_PART_V_ITEM = 'question745';
const FUGL_MEYER_MAX_POINTS = 2;

// Trunk Impairment Scale: the 17 items, in the order the backend scores them.
const TRUNK_IMPAIRMENT_SCALE_ITEMS = [
  // Static sitting balance
  'question807',
  'question808',
  'question809',
  // Dynamic sitting balance
  'question811',
  'question812',
  'question813',
  'question814',
  'question815',
  'question816',
  'question817',
  'question818',
  'question819',
  'question820',
  // Co-ordination
  'question822',
  'question823',
  'question824',
  'question825',
];

// A 0 on static item 1 makes the whole TIS 0: no other item is scored.
export const TRUNK_IMPAIRMENT_SCALE_FIRST_ITEM = 'question807';

// A 0 on an item zeroes the items that build on it.
const TRUNK_IMPAIRMENT_SCALE_ZEROED_ITEMS: Record<string, string[]> = {
  question811: ['question812', 'question813'],
  question812: ['question813'],
  question814: ['question815', 'question816'],
  question815: ['question816'],
  question817: ['question818'],
  question819: ['question820'],
  question822: ['question823'],
  question824: ['question825'],
};

// The points are the number in "(n)" at the start of the chosen answer.
const LEADING_POINTS_REGEX = /^\s*\(\s*(\d+)\s*\)/;
// A choice value such as "Item 2" is never a number of points.
const ITEM_VALUE_REGEX = /^\s*item\s*\d+\s*$/i;
const LEADING_INTEGER_REGEX = /^\s*(\d+)\b/;

// The points of an answer, read as the backend reads them: the "(n)" at the
// start of the chosen answer's text, the choice found by its value or by its
// text, never by its position. An answer that matches no choice may be the
// number itself.
export function getAnswerPoints(
  element: IQuestionnaireQuestion | undefined,
  value: unknown,
): number | null {
  const answer = value === null || value === undefined ? '' : String(value);
  if (!answer.trim()) {
    return null;
  }

  const choice = element?.choices?.find(
    (c) =>
      c.value === answer || c.text?.toLowerCase() === answer.toLowerCase(),
  );
  const match = (choice?.text ?? answer.trim()).match(LEADING_POINTS_REGEX);
  if (match) {
    return Number(match[1]);
  }

  const number = ITEM_VALUE_REGEX.test(answer)
    ? null
    : answer.match(LEADING_INTEGER_REGEX);
  return number ? Number(number[1]) : null;
}

// The TIS items an earlier 0 zeroes: every other item after a 0 on item 1,
// else the items that build on a 0. A zeroed item's own answer is ignored.
export function findTrunkImpairmentScaleLockedItems(
  elements: IQuestionnaireQuestion[],
  getAnswer: AnswerReader,
): Set<string> {
  const pointsOf = (item: string) =>
    getAnswerPoints(
      elements.find((el) => el.name === item),
      getAnswer(item),
    );

  if (pointsOf(TRUNK_IMPAIRMENT_SCALE_FIRST_ITEM) === 0) {
    return new Set(
      TRUNK_IMPAIRMENT_SCALE_ITEMS.filter(
        (item) => item !== TRUNK_IMPAIRMENT_SCALE_FIRST_ITEM,
      ),
    );
  }

  const locked = new Set<string>();
  for (const item of TRUNK_IMPAIRMENT_SCALE_ITEMS) {
    if (!locked.has(item) && pointsOf(item) === 0) {
      TRUNK_IMPAIRMENT_SCALE_ZEROED_ITEMS[item]?.forEach((dependent) =>
        locked.add(dependent),
      );
    }
  }
  return locked;
}

// Fugl-Meyer part V while part IV is not full.
export function findFuglMeyerLockedItems(
  elements: IQuestionnaireQuestion[],
  getAnswer: AnswerReader,
): Set<string> {
  const partIV = FUGL_MEYER_PART_IV_ITEMS.reduce((sum, item) => {
    const points = getAnswerPoints(
      elements.find((el) => el.name === item),
      getAnswer(item),
    );
    return points !== null && points <= FUGL_MEYER_MAX_POINTS
      ? sum + points
      : sum;
  }, 0);

  return partIV === FUGL_MEYER_PART_IV_FULL_SCORE
    ? new Set<string>()
    : new Set([FUGL_MEYER_PART_V_ITEM]);
}

// The items a questionnaire's rules lock; most questionnaires have none.
export function findLockedQuestionnaireItems(
  questionnaireTemplateId: number,
  elements: IQuestionnaireQuestion[],
  getAnswer: AnswerReader,
): Set<string> {
  switch (questionnaireTemplateId) {
    case FUGL_MEYER_TEMPLATE_ID:
      return findFuglMeyerLockedItems(elements, getAnswer);
    case TRUNK_IMPAIRMENT_SCALE_TEMPLATE_ID:
      return findTrunkImpairmentScaleLockedItems(elements, getAnswer);
    default:
      return new Set<string>();
  }
}

// The answers to save. An answer a locked item kept from before it was locked
// is left out: the item scores 0 without one, and the backend ignores it.
export function omitLockedAnswers(
  questionnaireTemplateId: number,
  elements: IQuestionnaireQuestion[],
  answers: Record<string, unknown>,
): Record<string, unknown> {
  const locked = findLockedQuestionnaireItems(
    questionnaireTemplateId,
    elements,
    (item) => answers[item],
  );

  return Object.fromEntries(
    Object.entries(answers).map(([name, value]) => [
      name,
      locked.has(name) ? null : value,
    ]),
  );
}
